import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { RankingRatio } from '@sorare/core/src/components/collections/RankingRatio';
import { Score } from '@sorare/core/src/components/collections/Score';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';

import type { CollectionPreviewStats_userCardCollection } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal)`
  justify-content: space-around;
`;

const BaselineHorizontal = styled(Horizontal).attrs({ gap: 0.5 })`
  align-items: baseline;
`;
type Props = {
  userCardCollection: CollectionPreviewStats_userCardCollection | null;
  shouldBeHighlighted?: boolean;
  snapshotted?: boolean;
};
export const CollectionPreviewStats = ({
  userCardCollection,
  shouldBeHighlighted,
  snapshotted,
}: Props) => {
  const defaults = {
    fulfilledSlotsCount: 0,
    score: 0,
    bonus: 0,
    liveRanking: 0,
    liveRankingRatio: null,
    cardCollection: {
      rarity: Rarity.common,
    },
  };
  const { score, liveRanking, liveRankingRatio, cardCollection } =
    userCardCollection || defaults;
  return (
    <Root>
      <OnboardingHighlightWrapper
        wrap={!!shouldBeHighlighted}
        highlightName="CollectionPreviewStatsRank"
      >
        <BaselineHorizontal gap={1} center>
          <LabelL bold>
            <RankingRatio
              liveRankingRatio={liveRankingRatio || 0}
              liveRanking={liveRanking}
            />
          </LabelL>
        </BaselineHorizontal>
      </OnboardingHighlightWrapper>
      {!snapshotted && (
        <OnboardingHighlightWrapper
          wrap={!!shouldBeHighlighted}
          highlightName="CollectionPreviewStatsScore"
        >
          <Score score={score} rarity={cardCollection?.rarity} />
        </OnboardingHighlightWrapper>
      )}
    </Root>
  );
};

CollectionPreviewStats.fragments = {
  userCardCollection: gql`
    fragment CollectionPreviewStats_userCardCollection on UserCardCollection {
      id
      score
      liveRanking
      liveRankingRatio
      cardCollection {
        slug
        rarity
      }
    }
  ` as TypedDocumentNode<CollectionPreviewStats_userCardCollection>,
};
